import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { geoguesserService, gameService } from '../../../services/api';
import FloorImageDropZone from './FloorImageDropZone';

/**
 * Map-level attributes (name, game, description, active, thumbnail,
 * legacy image) with per-field auto-save.
 *
 * This component is explicitly designed to be reusable in both the
 * migration wizard (where it's a read-only / light header summary)
 * and the map editor (full edit surface).
 *
 * Props:
 *   - `mapId`       – required.
 *   - `initialData` – optional pre-fetched map object; when provided
 *                     we skip the initial GET.
 *   - `games`       – optional pre-fetched games list; otherwise we
 *                     load it.
 *   - `readOnly`    – render disabled inputs, no auto-save.
 *   - `compact`     – tighter layout used as a wizard header.
 *   - `onMapChange(map)` – fired after every successful PATCH with
 *                     the authoritative map object.
 *   - `onError`, `onSuccess` – banner / toast hooks.
 */
const errorDetail = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'object' && data) {
    const entries = Object.entries(data);
    if (entries.length > 0) {
      return entries
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
        .join('; ');
    }
  }
  return data?.detail || fallback;
};

const MapDetailsForm = ({
  mapId,
  initialData,
  games: gamesProp,
  readOnly = false,
  compact = false,
  onMapChange,
  onError,
  onSuccess,
}) => {
  const [map, setMap] = useState(initialData || null);
  const [games, setGames] = useState(gamesProp || []);
  const [loading, setLoading] = useState(!initialData);
  const [buffer, setBuffer] = useState({
    name: initialData?.name || '',
    game: initialData?.game || initialData?.game_id || '',
    description: initialData?.description || '',
    is_active:
      initialData?.is_active !== undefined ? initialData.is_active : true,
  });
  const [fieldState, setFieldState] = useState({});

  const timersRef = useRef({});
  const bufferRef = useRef(buffer);
  useEffect(() => {
    bufferRef.current = buffer;
  }, [buffer]);

  const reportError = useCallback(
    (msg) => {
      if (typeof onError === 'function') onError(msg);
    },
    [onError],
  );
  const reportSuccess = useCallback(
    (msg) => {
      if (typeof onSuccess === 'function') onSuccess(msg);
    },
    [onSuccess],
  );

  // Initial load. If the caller already has the map + games, skip.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const tasks = [];
      if (!initialData && mapId) tasks.push(geoguesserService.getMapById(mapId));
      else tasks.push(null);
      if (!gamesProp) tasks.push(gameService.getAll(true));
      else tasks.push(null);
      try {
        const [mapRes, gamesRes] = await Promise.all(tasks);
        if (cancelled) return;
        if (mapRes?.data) {
          setMap(mapRes.data);
          setBuffer({
            name: mapRes.data.name || '',
            game: mapRes.data.game || mapRes.data.game_id || '',
            description: mapRes.data.description || '',
            is_active:
              mapRes.data.is_active !== undefined
                ? mapRes.data.is_active
                : true,
          });
        }
        if (gamesRes?.data) setGames(gamesRes.data);
      } catch (err) {
        if (!cancelled) {
          reportError(errorDetail(err, 'Failed to load map details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mapId, initialData, gamesProp, reportError]);

  const setFieldStatus = useCallback((key, next) => {
    setFieldState((prev) => {
      if (next == null) {
        if (!(key in prev)) return prev;
        const out = { ...prev };
        delete out[key];
        return out;
      }
      return { ...prev, [key]: next };
    });
  }, []);

  const doPatch = useCallback(
    async (key, payload) => {
      if (!mapId) return;
      setFieldStatus(key, 'saving');
      try {
        const res = await geoguesserService.patchMap(mapId, payload);
        const updated = res?.data || null;
        if (updated) {
          setMap(updated);
          if (typeof onMapChange === 'function') onMapChange(updated);
        }
        setFieldStatus(key, 'saved');
        window.setTimeout(() => {
          setFieldState((prev) => {
            if (prev[key] !== 'saved') return prev;
            const out = { ...prev };
            delete out[key];
            return out;
          });
        }, 1200);
      } catch (err) {
        setFieldStatus(key, 'error');
        reportError(errorDetail(err, 'Failed to save map.'));
      }
    },
    [mapId, onMapChange, reportError, setFieldStatus],
  );

  // Flush pending debounces on unmount so the last keystroke is saved.
  useEffect(() => {
    return () => {
      for (const [key, timer] of Object.entries(timersRef.current)) {
        window.clearTimeout(timer);
        const cur = bufferRef.current?.[key];
        if (cur != null && cur !== '') {
          const data = new FormData();
          data.append(key, String(cur));
          geoguesserService.patchMap(mapId, data).catch(() => {
            // eslint-disable-next-line no-console
            console.warn('Flush map field on unmount failed', key);
          });
        }
      }
      timersRef.current = {};
    };
  }, [mapId]);

  const scheduleTextSave = useCallback(
    (key, value) => {
      if (readOnly) return;
      if (timersRef.current[key]) window.clearTimeout(timersRef.current[key]);
      timersRef.current[key] = window.setTimeout(() => {
        delete timersRef.current[key];
        const trimmed = typeof value === 'string' ? value.trim() : value;
        if (trimmed === '' && key === 'name') return;
        const data = new FormData();
        data.append(key, trimmed ?? '');
        doPatch(key, data);
      }, 600);
    },
    [doPatch, readOnly],
  );

  const flushText = useCallback(
    (key) => {
      const timer = timersRef.current[key];
      if (!timer) return;
      window.clearTimeout(timer);
      delete timersRef.current[key];
      const value = bufferRef.current?.[key];
      if (value == null) return;
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (trimmed === '' && key === 'name') return;
      const data = new FormData();
      data.append(key, trimmed ?? '');
      doPatch(key, data);
    },
    [doPatch],
  );

  const handleTextChange = (key) => (e) => {
    const val = e.target.value;
    setBuffer((prev) => ({ ...prev, [key]: val }));
    scheduleTextSave(key, val);
  };

  const handleSelectChange = (key) => (e) => {
    const val = e.target.value;
    setBuffer((prev) => ({ ...prev, [key]: val }));
    if (readOnly) return;
    const data = new FormData();
    data.append(key, val);
    doPatch(key, data);
  };

  const handleCheckboxChange = (key) => (e) => {
    const val = e.target.checked;
    setBuffer((prev) => ({ ...prev, [key]: val }));
    if (readOnly) return;
    const data = new FormData();
    data.append(key, val ? 'true' : 'false');
    doPatch(key, data);
  };

  const handleImageUpload = (fieldName) => (file) => {
    if (readOnly || !file) return;
    const data = new FormData();
    data.append(fieldName, file);
    doPatch(fieldName, data);
    reportSuccess('Uploading new image…');
  };

  if (loading && !map) return <p className="wizard-help">Loading map…</p>;
  if (!map) return null;

  const renderChip = (key) => {
    const state = fieldState[key];
    if (!state) return null;
    const label =
      state === 'saving'
        ? 'Saving…'
        : state === 'saved'
        ? 'Saved'
        : 'Save failed';
    return (
      <span
        className={`map-details-chip is-${state}`}
        role="status"
        aria-live="polite"
      >
        {label}
      </span>
    );
  };

  return (
    <section
      className={`map-details-form ${compact ? 'is-compact' : 'is-full'}`}
    >
      <h3>Map details</h3>
      {!compact && (
        <p className="wizard-help">
          Everything auto-saves as you go — no Save button. Leave the
          legacy image blank for new maps; gameplay uses per-floor
          images.
        </p>
      )}

      <div className="map-details-grid">
        <div className="map-details-field">
          <label>
            Game
            <select
              value={buffer.game}
              onChange={handleSelectChange('game')}
              disabled={readOnly}
            >
              <option value="">Select a game</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          {renderChip('game')}
        </div>

        <div className="map-details-field">
          <label>
            Map name
            <input
              type="text"
              value={buffer.name}
              onChange={handleTextChange('name')}
              onBlur={() => flushText('name')}
              placeholder="e.g. Bank, Chalet"
              disabled={readOnly}
            />
          </label>
          {renderChip('name')}
        </div>

        <div className="map-details-field map-details-field-wide">
          <label>
            Description
            <textarea
              rows={compact ? 2 : 3}
              value={buffer.description}
              onChange={handleTextChange('description')}
              onBlur={() => flushText('description')}
              placeholder="Optional context for admins browsing maps"
              disabled={readOnly}
            />
          </label>
          {renderChip('description')}
        </div>

        <div className="map-details-field map-details-field-inline">
          <label className="map-details-checkbox">
            <input
              type="checkbox"
              checked={!!buffer.is_active}
              onChange={handleCheckboxChange('is_active')}
              disabled={readOnly}
            />
            Active (players can select this map)
          </label>
          {renderChip('is_active')}
        </div>

        {!compact && (
          <>
            <div className="map-details-field map-details-image-slot">
              <div className="map-details-image-label">
                <span className="map-details-image-title">Thumbnail</span>
                <span className="map-details-image-hint">
                  Shown on map cards. Falls back to the first floor
                  image if empty.
                </span>
              </div>
              <FloorImageDropZone
                preview={map.thumbnail || null}
                emptyLabel="Drop thumbnail or click to browse"
                ariaLabel="Upload thumbnail"
                onFileSelected={handleImageUpload('thumbnail')}
              />
              {renderChip('thumbnail')}
            </div>

            <div className="map-details-field map-details-image-slot">
              <div className="map-details-image-label">
                <span className="map-details-image-title">
                  Legacy map image
                </span>
                <span className="map-details-image-hint">
                  Deprecated — gameplay uses per-floor images. Kept as
                  a reference for old answers.
                </span>
              </div>
              <FloorImageDropZone
                preview={map.map_image || null}
                emptyLabel="Drop legacy image or click to browse"
                ariaLabel="Upload legacy map image"
                onFileSelected={handleImageUpload('map_image')}
              />
              {renderChip('map_image')}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default MapDetailsForm;
